/* eslint-disable react/jsx-props-no-spreading */
import React, {
  useState,
  useCallback,
  useEffect,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useLazyQuery } from '@apollo/client';
import { useHistory } from 'react-router-dom';

// Modals
import CustomModal from 'modals/CustomModal';

// Components
import Dropdown from 'components/Dropdown';
import Button, { ButtonTypes } from 'components/Button';

// Queries
import { getListSchools } from 'data/school';

export interface SelectSchoolDashboardModalProps {
  isOpen: boolean;
  toggle: () => void;
  hideClose?: boolean;
}

const SelectSchoolDashboardModal = React.memo(({
  isOpen,
  toggle,
  ...props
}: SelectSchoolDashboardModalProps) => {
  const { t } = useTranslation();
  const [formState, setFormState] = useState({ school: undefined });
  const history = useHistory();
  const [schoolOptions, setSchoolOptions] = useState<{ value: string; label: string; }[]>([]);

  // Queries
  const [callGetListSchools] = useLazyQuery<GetListSchoolsResponse>(getListSchools, {
    fetchPolicy: 'network-only',
    onCompleted: data => {
      const options = data.schools.map(school => ({ value: school.id, label: school.name }));
      setSchoolOptions(options);
    },
  });

  useEffect(() => {
    if (isOpen) {
      callGetListSchools();
    }
  }, [isOpen]);

  const handleSelectChange = useCallback((key: string, value: OptionType | undefined) => {
    setFormState(s => ({
      ...s,
      [key]: value,
    }));
  }, []);

  const handleClose = useCallback(() => {
    setFormState({ school: undefined });
    toggle();
  }, []);

  const handleGo = useCallback(() => {
    toggle();
    history.push('/dashboard', { school: formState.school });
  }, [formState]);

  return (
    <CustomModal
      {...props}
      isOpen={isOpen}
      toggle={handleClose}
      title={t('SELECT_SCHOOL_DASHBOARD.SWITCH_SCHOOL')}
    >
      <Dropdown
        title={t('SELECT_SCHOOL_DASHBOARD.SELECT')}
        onSelect={handleSelectChange}
        selected={formState.school}
        options={schoolOptions}
        name="school"
      />

      <div className="d-flex justify-content-end mt-5">
        <Button
          onClick={handleClose}
          type={ButtonTypes.SECONDARY}
          minWidth="130px"
          className="mr-3"
        >
          {t('SELECT_SCHOOL_DASHBOARD.CANCEL')}
        </Button>
        <Button
          onClick={handleGo}
          type={ButtonTypes.PRIMARY}
          disabled={formState.school === undefined}
          minWidth="130px"
        >
          {t('SELECT_SCHOOL_DASHBOARD.GO')}
        </Button>
      </div>
    </CustomModal>
  );
});

export default SelectSchoolDashboardModal;
